import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { useLanguage } from '../../context/LanguageContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Pencil, Eye, CreditCard } from 'lucide-react';

const STATUS_BADGE = {
  pending: { color: 'bg-yellow-100 text-yellow-800', label: 'doc_status_pending' },
  approved: { color: 'bg-emerald-100 text-emerald-800', label: 'doc_status_approved' },
  rejected: { color: 'bg-red-100 text-red-800', label: 'doc_status_rejected' },
};

export default function AdminResidenceCardPage() {
  const { t } = useLanguage();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDoc, setViewDoc] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try {
      const res = await api.getAdminResidenceCards();
      setDocs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleReview = async (docId, action) => {
    try {
      await api.reviewAdminResidenceCard(docId, action, reviewNotes);
      toast.success(t(action === 'approve' ? 'admin_doc_approved' : 'admin_doc_rejected'));
      setViewDoc(null);
      setReviewNotes('');
      fetchDocs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleEdit = async () => {
    try {
      await api.updateAdminResidenceCard(editDoc.id, editForm);
      toast.success(t('admin_doc_updated'));
      setEditDoc(null);
      fetchDocs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openEdit = (doc) => {
    setEditDoc(doc);
    setEditForm({ card_number: doc.card_number, expiry_date: doc.expiry_date });
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <AdminLayout>
      <div data-testid="admin-residence-card-page">
        <h1 className="font-heading text-3xl font-bold text-primary mb-2">{t('admin_residence_card_title')}</h1>
        <p className="text-muted-foreground mb-8">{t('admin_residence_card_desc')}</p>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : docs.length === 0 ? (
          <div className="text-center py-16">
            <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">{t('admin_no_documents')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto bg-card border border-border/50 rounded-2xl">
            <table className="w-full text-sm" data-testid="residence-card-table">
              <thead>
                <tr className="border-b text-left">
                  <th className="px-4 py-3 font-semibold">{t('admin_doc_user')}</th>
                  <th className="px-4 py-3 font-semibold">{t('residence_card_number')}</th>
                  <th className="px-4 py-3 font-semibold">{t('residence_card_expiry')}</th>
                  <th className="px-4 py-3 font-semibold">{t('admin_doc_status')}</th>
                  <th className="px-4 py-3 font-semibold">{t('doc_submitted_at')}</th>
                  <th className="px-4 py-3 font-semibold">{t('admin_doc_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => {
                  const badge = STATUS_BADGE[doc.status] || STATUS_BADGE.pending;
                  return (
                    <tr key={doc.id} className="border-b hover:bg-muted/30" data-testid={`residence-card-row-${doc.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{doc.user_name}</div>
                        <div className="text-xs text-muted-foreground">{doc.user_email}</div>
                      </td>
                      <td className="px-4 py-3 font-mono">{doc.card_number}</td>
                      <td className="px-4 py-3">{doc.expiry_date}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>{t(badge.label)}</span>
                      </td>
                      <td className="px-4 py-3 text-xs">{new Date(doc.submitted_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => { setViewDoc(doc); setReviewNotes(''); }} data-testid={`view-residence-card-${doc.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(doc)} data-testid={`edit-residence-card-${doc.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* View / Review Dialog */}
        <Dialog open={!!viewDoc} onOpenChange={(o) => { if (!o) setViewDoc(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('admin_residence_card_review')}</DialogTitle>
              <DialogDescription>{viewDoc?.user_name} ({viewDoc?.user_email})</DialogDescription>
            </DialogHeader>
            {viewDoc && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground mb-1 block">{t('residence_card_front')}</Label>
                    {(viewDoc.front_photo_url || viewDoc.photo_url) && (
                      <img src={`${API_URL}${viewDoc.front_photo_url || viewDoc.photo_url}`} alt="Front" className="w-full max-h-52 object-contain rounded-lg border" data-testid="admin-residence-card-front-image" />
                    )}
                  </div>
                  <div>
                    <Label className="text-muted-foreground mb-1 block">{t('residence_card_back')}</Label>
                    {viewDoc.back_photo_url ? (
                      <img src={`${API_URL}${viewDoc.back_photo_url}`} alt="Back" className="w-full max-h-52 object-contain rounded-lg border" data-testid="admin-residence-card-back-image" />
                    ) : (
                      <div className="w-full h-32 rounded-lg border border-dashed flex items-center justify-center text-muted-foreground text-sm">{t('doc_status_not_submitted')}</div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-muted-foreground">{t('residence_card_number')}</Label><p className="font-semibold font-mono">{viewDoc.card_number}</p></div>
                  <div><Label className="text-muted-foreground">{t('residence_card_expiry')}</Label><p className="font-semibold">{viewDoc.expiry_date}</p></div>
                </div>
                {viewDoc.status === 'pending' && (
                  <div className="space-y-3 border-t pt-4">
                    <div className="space-y-1.5">
                      <Label>{t('doc_reviewer_notes')}</Label>
                      <Textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} data-testid="review-notes-input" />
                    </div>
                    <div className="flex gap-3">
                      <Button onClick={() => handleReview(viewDoc.id, 'approve')} className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="approve-residence-card-btn">
                        <CheckCircle className="h-4 w-4 mr-2" />{t('admin_doc_approve')}
                      </Button>
                      <Button variant="destructive" onClick={() => handleReview(viewDoc.id, 'reject')} className="flex-1 rounded-full" data-testid="reject-residence-card-btn">
                        <XCircle className="h-4 w-4 mr-2" />{t('admin_doc_reject')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={!!editDoc} onOpenChange={(o) => { if (!o) setEditDoc(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('admin_residence_card_edit')}</DialogTitle>
              <DialogDescription>{editDoc?.user_name}</DialogDescription>
            </DialogHeader>
            {editDoc && (
              <div className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label>{t('residence_card_number')}</Label>
                  <Input value={editForm.card_number ?? ''} onChange={e => setEditForm(p => ({ ...p, card_number: e.target.value }))} data-testid="edit-card-number-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('residence_card_expiry')}</Label>
                  <Input type="date" value={editForm.expiry_date ?? ''} onChange={e => setEditForm(p => ({ ...p, expiry_date: e.target.value }))} data-testid="edit-expiry-date-input" />
                </div>
                <Button onClick={handleEdit} className="w-full rounded-full" data-testid="save-edit-residence-card-btn">
                  {t('recipients_save')}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
